import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { articlesApi, categoriesApi, tagsApi, type ArticleListParams } from '@/services/articles';
import type { ArticleUpdate } from '@/types/article';

export const articleKeys = {
  all: ['articles'] as const,
  list: (params: ArticleListParams) => [...articleKeys.all, 'list', params] as const,
  detail: (id: number) => [...articleKeys.all, 'detail', id] as const,
  history: (id: number) => [...articleKeys.all, 'history', id] as const,
};

export const categoryKeys = {
  all: ['categories'] as const,
  site: (site: string) => ['categories', site] as const,
};

export const tagKeys = {
  all: ['tags'] as const,
  search: (q: string) => [...tagKeys.all, 'search', q] as const,
};

export function useArticles(params: ArticleListParams = {}) {
  return useQuery({
    queryKey: articleKeys.list(params),
    queryFn: () => articlesApi.list(params),
    refetchInterval: 120_000,
  });
}

export function useArticle(id: number | undefined, site?: string) {
  return useQuery({
    queryKey: [...articleKeys.detail(id ?? 0), site ?? ''] as const,
    queryFn: () => articlesApi.get(id!, site),
    enabled: Boolean(id),
  });
}

export function useArticleHistory(id: number | undefined) {
  return useQuery({
    queryKey: articleKeys.history(id ?? 0),
    queryFn: () => articlesApi.history(id!),
    enabled: Boolean(id),
  });
}

export function useUpdateArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
      site,
    }: {
      id: number;
      payload: ArticleUpdate;
      site?: string;
    }) => articlesApi.update(id, payload, site),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: articleKeys.detail(id) });
      qc.invalidateQueries({ queryKey: articleKeys.history(id) });
      qc.invalidateQueries({ queryKey: articleKeys.all });
    },
  });
}

export function useCategories(site?: string) {
  return useQuery({
    queryKey: site ? categoryKeys.site(site) : categoryKeys.all,
    queryFn: () => categoriesApi.list(site),
    staleTime: 60 * 60_000,
  });
}

export function useTagsSearch(query: string, site?: string, enabled = true) {
  return useQuery({
    queryKey: [...tagKeys.search(query), site ?? ''] as const,
    queryFn: () => tagsApi.list(query || undefined, site),
    enabled: enabled && query.length >= 2,
    staleTime: 60_000,
  });
}

export function useTagsByIds(ids: number[], site?: string) {
  const sortedKey = [...ids].sort((a, b) => a - b).join(',');
  return useQuery({
    queryKey: [...tagKeys.all, 'byIds', sortedKey, site ?? ''] as const,
    queryFn: () => tagsApi.byIds(ids, site),
    enabled: ids.length > 0,
    staleTime: 5 * 60_000,
  });
}
